import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Loading from "../../Common/Loading";
import VbGrid, { VbGridColumn } from "../../Common/Vbgrid";
import CustomAlert from "../../Common/CustomAlert";

const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;
const REFRESH_MS = 60000; // VB: window.setTimeout(fn_Refresh, 60000) – re-armed on every page load
const NO_DATA_MESSAGE = "No data records found.";

interface LoginRow {
  country: string;
  schoolCode: string;
  familyCode: string;
  login: string;
  name: string;
  catg: string;
  from: string;
  loggedAt: string;
  lastUsed: string;
  activity: string;
  lastUsedPage: string;
  status: string;
  browser: string;
  trColor: string; // "" | "red"
}

interface ApiResponse {
  currentTime: string;
  title: string;
  rows: LoginRow[];
  activeRows: number;
  unauthorisedUsage: string;
  errorMessage: string;
}

// VB: dtN columns are added with their names only (no Header array) -> the grid headers ARE
// the column names, and every column is a String column -> left aligned, string sorting.
const FIELDS: { key: keyof LoginRow; col: VbGridColumn }[] = [
  { key: "country", col: { header: "Country", type: "S" } },
  { key: "schoolCode", col: { header: "SchoolCode", type: "S" } },
  { key: "familyCode", col: { header: "FamilyCode", type: "S" } },
  { key: "login", col: { header: "Login", type: "S" } },
  { key: "name", col: { header: "Name", type: "S" } },
  { key: "catg", col: { header: "Catg", type: "S" } },
  { key: "from", col: { header: "From", type: "S" } },
  { key: "browser", col: { header: "Browser", type: "S" } },
  { key: "loggedAt", col: { header: "LoggedAt", type: "S" } },
  { key: "lastUsed", col: { header: "LastUsed", type: "S" } },
  { key: "activity", col: { header: "Activity", type: "S", html: true } },
  { key: "lastUsedPage", col: { header: "LastUsedPage", type: "S" } },
  { key: "status", col: { header: "Status", type: "S" } },
];
const GRID_COLUMNS = FIELDS.map((f) => ({ ...f.col, align: "left" as const }));

const EL_ShowCurrentLogins: React.FC = () => {
  const [data, setData] = useState<ApiResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [bodyCleared, setBodyCleared] = useState(false);
  const [selIdx, setSelIdx] = useState<number | null>(null);
  const [gridSize, setGridSize] = useState({ w: 800, h: 400 });

  const hidSchoolCode = useRef("");
  const hidFamilyCode = useRef("");
  const hidLogin = useRef("");
  const hidSchoolCategory = useRef("");

  const table0Ref = useRef<HTMLTableElement>(null);
  const table1Ref = useRef<HTMLTableElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Mirror of bodyCleared for use inside fetchData's finally block
  // (VB clears the body on unauthorised usage, so the refresh must not be re-armed).
  const bodyClearedRef = useRef(false);
  useEffect(() => {
    bodyClearedRef.current = bodyCleared;
  }, [bodyCleared]);


    useEffect(() => {
      document.body.style.backgroundColor = "#fff";
    }, []);

  // Single entry point for every alert on this page (rendered by the imported CustomAlert).
  const showMsg = useCallback((msg: string) => {
    if (msg) setMessage(msg);
  }, []);

  const closeMsg = useCallback(() => {
    setMessage("");
  }, []);

  const clearTimer = () => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
  };

  // Equivalent of a full ASP.NET postback/page load:
  //  - grid is rebuilt (client-side selection and sort are lost)
  //  - the 60-second auto-refresh timer is re-armed
  const fetchData = useCallback(async () => {
    clearTimer();
    try {
      const res = await fetch(`${API_BASE_URL}api/current-logins/`);
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      const json: ApiResponse = await res.json();

      if (json.unauthorisedUsage) {
        bodyClearedRef.current = true;
        setBodyCleared(true);
        showMsg(json.unauthorisedUsage);
        return; // VB clears the body, so no further refresh
      }

      const rows = json.rows ?? [];
      setSelIdx(null);
      setData({ ...json, rows });

      if (json.errorMessage) {
        showMsg(json.errorMessage);
      } else if (rows.length === 0) {
        showMsg(NO_DATA_MESSAGE);
      }
    } catch (e) {
      showMsg(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
      if (!bodyClearedRef.current) {
        timerRef.current = setTimeout(fetchData, REFRESH_MS);
      }
    }
  }, [showMsg]);

  useEffect(() => {
    fetchData();
    return clearTimer;
  }, [fetchData]);

  // VB fn_Load: width = clientWidth-20, height = clientHeight - Table0 - Table1 - 50
  useEffect(() => {
    const resize = () => {
      if (!table0Ref.current || !table1Ref.current) return;
      setGridSize({
        w: window.innerWidth - 20,
        h: Math.max(60, window.innerHeight - table0Ref.current.offsetHeight - table1Ref.current.offsetHeight - 50),
      });
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, [data]);

  const gridRows = useMemo(
    () => (data?.rows ?? []).map((r) => FIELDS.map((f) => r[f.key] ?? "")),
    [data]
  );
  const rowColors = useMemo(() => (data?.rows ?? []).map((r) => r.trColor || ""), [data]);

  // Kept from VB (fn_IsRowSelected) – not wired to any button in the original either.
  const fn_IsRowSelected = (): boolean => {
    const row = selIdx !== null ? data?.rows[selIdx] : undefined;
    if (!row) {
      showMsg("Please select a row.");
      return false;
    }
    hidSchoolCode.current = row.schoolCode;
    hidFamilyCode.current = row.familyCode;
    hidLogin.current = row.login;
    hidSchoolCategory.current = row.catg;
    return true;
  };
  void fn_IsRowSelected;

  const handleBack = () => {
    window.location.href = "/SysAdmin";
  };

  const handleRefresh = () => {
    setLoading(true);
    fetchData();
  };

  const handleClearIdle = async () => {
    setLoading(true);
    try {
      await fetch(`${API_BASE_URL}api/current-logins/clear-idle/`, { method: "POST" });
    } catch { /* non-fatal */ }
    handleRefresh();
  };

  const handleGraph = () => {
    window.location.href = "/WEBADMIN/REPORTS/EL_UserCountGraph";
  };

  // Body cleared (unauthorised usage): only the alert remains, as in VB.
  if (bodyCleared) {
    return message ? <CustomAlert message={message} onClose={closeMsg} /> : null;
  }

  const styles = `
    body { margin: 0; padding: 0; font-family: 'Trebuchet MS', sans-serif; font-size: 10pt; }
    .cssLabelFixed {
      background-color: #2f6089;
      color: white;
      height: 22px;
      font-size: 9pt;
      font-family: 'Trebuchet MS', sans-serif;
      padding-left: 7px;
    }
    .cssButton {
      background-color: #e0e0e0;
      font-family: 'Trebuchet MS', sans-serif;
      font-size: 8pt;
      color: black;
      border: 1px solid black;
      border-radius: 3px;
      box-sizing: border-box;
      height: 22px;
      width: 80px;
      cursor: pointer;
    }
    .btnWide {
      background-color: #e0e0e0;
      font-family: 'Trebuchet MS', sans-serif;
      font-size: 8pt;
      color: black;
      border: 1px solid black;
      box-sizing: border-box;
      border-radius: 3px;
      height: 22px;
      width: 110px;
      cursor: pointer;
      margin-left: 4px;
    }
  `;

  if (loading && !data) {
    return (
      <>
        <style>{styles}</style>
        <div style={{ padding: 20, fontFamily: "Trebuchet MS", fontSize: "10pt" }}>
          <Loading />
        </div>
        {message && <CustomAlert message={message} onClose={closeMsg} />}
      </>
    );
  }

  return (
    <>
      <style>{styles}</style>

      {message && <CustomAlert message={message} onClose={closeMsg} />}

      {/* Loading overlay for Refresh / Clear Idle Seats / auto-refresh-triggered reloads */}
      {loading && data && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 99998,
          background: "rgba(255,255,255,0.6)", display: "flex",
          alignItems: "center", justifyContent: "center",
        }}>
          <Loading />
        </div>
      )}

      {/* ── Table0: header bar (maroon) – title left, time right (as in VB) ── */}
      <table
        id="Table0"
        ref={table0Ref}
        border={0}
        cellPadding={5}
        cellSpacing={0}
        style={{
          width: "100%",
          backgroundColor: "maroon",
          color: "white",
          fontWeight: "bold",
          fontFamily: "Calibri, sans-serif",
          fontSize: "14pt",
        }}
      >
        <tbody>
          <tr>
            <td align="left">List of Logged in users</td>
            <td align="right">{data?.currentTime ?? ""}</td>
          </tr>
        </tbody>
      </table>

      {/* ── Table2: holds the grid (title, header, rows) ── */}
      <table id="Table2" border={0} cellPadding={0} cellSpacing={0} style={{ width: "100%", backgroundColor: "#EEEEEE" }}>
        <tbody>
          <tr>
            <td>
              <VbGrid
                title={data?.title ?? ""}
                columns={GRID_COLUMNS}
                rows={gridRows}
                rowTextColors={rowColors}
                selectable
                selectedIndex={selIdx}
                onSelect={setSelIdx}
                width={gridSize.w}
                height={gridSize.h}
              />
            </td>
          </tr>
        </tbody>
      </table>

      <table
        id="Table1"
        ref={table1Ref}
        border={0}
        cellPadding={5}
        cellSpacing={0}
        style={{ width: "100%" }}
      >
        <tbody>
          <tr>
            <td className="cssLabelFixed" align="left" valign="middle" style={{ fontSize: "14pt" }}>
              <button className="cssButton" onClick={handleBack} style={{ width: 100 }}>
                Back
              </button>
            </td>
            <td className="cssLabelFixed" align="right" valign="middle">
              <button className="btnWide" onClick={handleClearIdle}>
                Clear Idle Seats
              </button>
              &nbsp;&nbsp;&nbsp;&nbsp;
              <button className="cssButton" onClick={handleRefresh} style={{ width: 100 }}>
                Refresh
              </button>
              {/* T3889:13/09/2024 */}
              <button className="btnWide" onClick={handleGraph} style={{ marginLeft: 4 }}>
                Show Graph
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </>
  );
};

export default EL_ShowCurrentLogins;